import {
  FEAASComponentProps,
  FEAASElement,
  fetchComponent,
  getComponentURL,
  getStylesheetURL,
  parseComponentSource
} from './index.js'
import React from 'react'
import { renderHTMLContent, HTMLNode, parseHTMLContent } from '../dom/html.js'
import { DataSettings } from '../utils/settings.js'
export { renderHTMLContent }
import { getComponent } from './FEAASExternal.js'
export { External, linkComponents, registerComponent, getComponent } from './FEAASExternal.js'
export * from './index.js'
export * from '../headless.js'

export type FEAASReactComponentProps = FEAASComponentProps & {
  preload?: boolean
  lastModified?: Date | string
}
export const ServerComponent = async (props: FEAASReactComponentProps) => {
  const fetchedData = await DataSettings.fetch(props?.data || {})

  const p: FEAASReactComponentProps = { ...props }

  if (('src' in props || 'component' in props) && props?.template == null) {
    const response = await fetchComponent(props)

    p.template = response.template
    p.lastModified = response.lastModified
  }

  return FEAASReactComponent({ ...p, data: fetchedData })
}

export function FEAASReactComponent({ data, ...props }: FEAASReactComponentProps) {
  let html = ''
  let preload = null
  let children: null | React.ReactNode

  if ('template' in props && props.template) {
    children = parseHTMLContent(props.template, data || {}).map((node, index) =>
      HTMLNodeToReact(
        node,
        (node) => {
          if (node.localName == 'feaas-external' && getComponent(node.getAttribute('data-external-id'))) {
            return [FEAASReactExternal, {}]
          }
        },
        index
      )
    )
  }

  if ('src' in props) {
    props = { ...parseComponentSource(props.src), ...props }
  }

  return (
    <>
      {'preload' in props && preload === true && 'src' in props && (
        <>
          <link rel='preload' as='fetch' href={getComponentURL(props.src)} crossOrigin='anonymous' />
          <link rel='preload' as='style' href={getStylesheetURL(props.src)} crossOrigin='anonymous' />
        </>
      )}
      <feaas-component
        {...props}
        suppressHydrationWarning
        template={null}
        data={typeof data == 'string' ? data : data ? JSON.stringify(data) : null}
      >
        {children}
      </feaas-component>
    </>
  )
}

export function FEAASReactExternal(props: { 'data-external-id': string }) {
  const Component = getComponent(props['data-external-id'])?.component
  // execute component as a function to supress hydration errors inside
  return (
    <feaas-external {...props} suppressHydrationWarning>
      {typeof Component == 'function' ? Component({ ...props }) : <Component {...props} />}
    </feaas-external>
  )
}

/** Turn HTMLNode into React tree. It accepts callback to customize the behavior */
export function HTMLNodeToReact(
  node: HTMLNode,
  callback?: (node: HTMLNode) => [tagName: any, attributes: Record<string, any>],
  key?: any
): React.ReactNode {
  if (node.nodeType === 3) {
    return node.textContent
  }
  const children = Array.from(node.childNodes).map((child, index) => HTMLNodeToReact(child, callback, index))

  const attributes: { [key: string]: string | Record<string, string> } = {}
  for (let i = 0; i < node.attributes.length; i++) {
    const { name, value } = node.attributes[i]
    if (name == 'style') {
      attributes[name] = String(value || '')
        .trim()
        .split(/\s*;\s*/)
        .reduce((object, pair) => {
          const [k, v] = pair.split(':')
          return {
            ...object,
            [k]: v
          }
        }, {})
    } else {
      attributes[name] = value
    }
  }

  const reactNode = callback?.(node)
  const reactName = reactNode?.[0] || node.nodeName.toLowerCase()
  const reactAttributes = Object.assign({ key, suppressHydrationWarning: true }, attributes, reactNode?.[1] || {})
  return React.createElement(reactName, reactAttributes, ['img', 'br', 'input'].includes(reactName) ? null : children)
}

export const Component = FEAASReactComponent
